import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, Phone, ChevronDown, ShieldCheck } from "lucide-react";
import { nav, company } from "../mock/mock";
import { Button } from "./ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetClose } from "./ui/sheet";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [openSol, setOpenSol] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const isActive = (to) => location.pathname === to;

  return (
    <header
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white/95 backdrop-blur border-b border-slate-200 shadow-sm" : "bg-white border-b border-transparent"
      }`}
    >
      <nav className="container-x flex h-16 items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-2.5">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand-navy">
            <ShieldCheck className="h-5 w-5 text-brand-teal" />
          </span>
          <span className="text-xl font-bold font-display tracking-tight text-brand-navy">
            Verifi<span className="text-brand-600">Assist</span>
          </span>
        </Link>

        {/* Desktop nav */}
        <div className="hidden lg:flex items-center gap-1">
          {nav.primary.map((item) =>
            item.children ? (
              <div
                key={item.label}
                className="relative"
                onMouseEnter={() => setOpenSol(true)}
                onMouseLeave={() => setOpenSol(false)}
              >
                <button className="flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-medium text-slate-700 transition-colors hover:text-brand-600">
                  {item.label}
                  <ChevronDown className={`h-4 w-4 transition-transform ${openSol ? "rotate-180" : ""}`} />
                </button>
                {openSol && (
                  <div className="absolute left-1/2 top-full z-50 w-[380px] -translate-x-1/2 pt-3">
                    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white p-2 shadow-xl">
                      {item.children.map((c) => (
                        <Link
                          key={c.to}
                          to={c.to}
                          className="flex flex-col rounded-xl px-4 py-3 transition-colors hover:bg-brand-50"
                        >
                          <span className="text-sm font-semibold text-brand-navy">{c.label}</span>
                          <span className="text-xs text-slate-500">{c.desc}</span>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <Link
                key={item.to}
                to={item.to}
                className={`rounded-lg px-3 py-2 text-sm font-medium transition-colors hover:text-brand-600 ${
                  isActive(item.to) ? "text-brand-600" : "text-slate-700"
                }`}
              >
                {item.label}
              </Link>
            )
          )}
        </div>

        <div className="hidden lg:flex items-center gap-3">
          <a href={`tel:${company.phoneRaw}`} className="flex items-center gap-2 text-sm font-semibold text-brand-navy hover:text-brand-600">
            <Phone className="h-4 w-4" />
            {company.phone}
          </a>
          <Button asChild className="bg-brand-600 hover:bg-brand-700">
            <Link to="/contact-us">Free Audit</Link>
          </Button>
        </div>

        {/* Mobile */}
        <div className="lg:hidden">
          <Sheet>
            <SheetTrigger asChild>
              <button aria-label="Open menu" className="flex h-10 w-10 items-center justify-center rounded-lg border border-slate-200 text-brand-navy">
                <Menu className="h-5 w-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[85%] sm:w-96 overflow-y-auto">
              <div className="mt-6 flex flex-col gap-1">
                {nav.primary.map((item) =>
                  item.children ? (
                    <div key={item.label} className="py-2">
                      <div className="px-3 pb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">{item.label}</div>
                      {item.children.map((c) => (
                        <SheetClose asChild key={c.to}>
                          <Link to={c.to} className="block rounded-lg px-3 py-2.5 text-sm font-medium text-slate-700 hover:bg-brand-50 hover:text-brand-600">
                            {c.label}
                          </Link>
                        </SheetClose>
                      ))}
                    </div>
                  ) : (
                    <SheetClose asChild key={item.to}>
                      <Link to={item.to} className="rounded-lg px-3 py-2.5 text-sm font-semibold text-slate-800 hover:bg-brand-50 hover:text-brand-600">
                        {item.label}
                      </Link>
                    </SheetClose>
                  )
                )}
                <div className="mt-4 border-t border-slate-200 pt-4">
                  <a href={`tel:${company.phoneRaw}`} className="flex items-center gap-2 px-3 py-2 text-sm font-semibold text-brand-navy">
                    <Phone className="h-4 w-4" /> {company.phone}
                  </a>
                  <SheetClose asChild>
                    <Button asChild className="mt-2 w-full bg-brand-600 hover:bg-brand-700">
                      <Link to="/contact-us">Get a Free Audit</Link>
                    </Button>
                  </SheetClose>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </nav>
    </header>
  );
}
